import { useEffect, useRef } from "react";

import moment from "moment";

require("moment/locale/de");

export const numFormatter = (data) => {
  return parseFloat(data).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const removeFromObj = (obj, keys) => {
  obj = { ...obj };
  Object.keys(obj)
    .filter((key) => keys.includes(key))
    .forEach((key) => delete obj[key]);
  return obj;
};

export const popFromObj = (obj, key) => {
  if (!(key in obj)) return null;
  const val = obj[key];
  delete obj[key];
  return val;
};

export const removeFromArr = (arr, condition) => {
  const newArr = [];
  let item = null;
  arr.forEach((x) => {
    if (item === null && condition(x)) item = x;
    else newArr.push(x);
  });
  return [item, newArr];
};

export const filterObj = (obj, keys) => {
  const keyCondition =
    typeof keys === "function" ? keys : (key) => keys.includes(key);
  obj = { ...obj };
  Object.keys(obj)
    .filter((key) => !keyCondition(key))
    .forEach((key) => delete obj[key]);
  return obj;
};

export const splitList = (arr, condition) => {
  const objs1 = [];
  const objs2 = [];
  arr.forEach((item) => {
    if (condition(item)) objs1.push(item);
    else objs2.push(item);
  });
  return [objs1, objs2];
};

export const isString = (v) => typeof v === "string" || v instanceof String;

export const isFunction = (v) => typeof v === "function";

export const isObject = (v) => v !== null && typeof v === "object";

export const defaultIfEmpty = (value) =>
  value === null || value === undefined ? "" : value;

export const round = (number, decimal = 0) =>
  Math.round(number * 10 ** decimal) / 10 ** decimal;

export const removeDuplicates = (arr) => [...new Set(arr)];

export const arrSum = (arr) => arr.reduce((pv, cv) => pv + cv, 0);

export const arrange = (start, end = null, step = 1) => {
  if (end === null) {
    end = start;
    start = 0;
  }
  const arr = [];
  for (let i = start; i < end; i += step) arr.push(i);
  return arr;
};

export const fillString = (string, len) => {
  const strLen = string.length;
  const diff = len - strLen;
  if (diff <= 0) return string;
  return " ".repeat(diff + 1) + string;
};

export const filledNumFormatter = (data, len) =>
  fillString(numFormatter(data), len);

export const time2String = (date) =>
  date && !isNaN(date.valueOf()) ? moment(date).format("HH:mm") : null;
export const datetime2String = (date) =>
  date && !isNaN(date.valueOf())
    ? moment(date).format("YYYY-MM-DDTHH:mm:ss")
    : null;
export const date2String = (date) =>
  date && !isNaN(date.valueOf()) ? moment(date).format("YYYY-MM-DD") : null;
export const month2String = (date) =>
  date && !isNaN(date.valueOf()) ? moment(date).format("YYYY-MM") : null;
export const year2String = (date) =>
  date && !isNaN(date.valueOf()) ? moment(date).format("YYYY") : null;

export const string2Time = (string) => {
  if (!string) return null;
  const _date = moment(string, "HH:mm").toDate();
  _date.setYear(1900);
  _date.setMonth(0);
  _date.setDate(1);
  return _date;
};
export const string2Datetime = (string) =>
  string ? moment(string, "YYYY-MM-DD HH-mm-ss").toDate() : null;
export const string2Date = (string) =>
  string ? moment(string, "YYYY-MM-DD").toDate() : null;

export const time2FormattedString = (date) => formatDate(date, "HH:mm");
export const datetime2FormattedString = (date, yearFormat = "YYYY") =>
  formatDate(date, `DD.MM.${yearFormat || ""} HH:mm`);
export const date2FormattedString = (
  date,
  yearFormat = "YYYY", // year 'YYYY
) => formatDate(date, "DD.MM." + (yearFormat || ""));
export const month2FormattedString = (
  date,
  monthFormat = "MM",
  yearFormat = "YYYY",
) =>
  formatDate(
    date,
    [monthFormat, yearFormat]
      .filter((x) => x)
      .join(monthFormat === "MM" ? "/" : " "),
  );
export const year2FormattedString = year2String;

export const formatDate = (date, format) =>
  date && !isNaN(date.valueOf())
    ? moment(date).locale("de").format(format)
    : "";

export const string2FormattedString = (string) =>
  string ? date2FormattedString(string2Date(string)) : null;
export const formattedString2Date = (string) =>
  string ? moment(string, "DD.MM.YYYY").toDate() : null;

export const getRandomId = () =>
  Math.floor(Math.random() * (10000000 - 1 + 1) + 1);

export const splitIntoBatches = (arr, nItems) => {
  if (arr.length === 0) return [];
  const batches = [[]];
  arr.forEach((i) => {
    const lastBatch = batches[batches.length - 1];
    if (lastBatch.length < nItems) lastBatch.push(i);
    else batches.push([i]);
  });
  return batches;
};

export function usePreviousValue(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export function useDidMountEffect(func, deps) {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) func();
    else didMount.current = true;
  }, deps);
}

export const isValidEmailAddress = (address) => {
  if (address != null) return !!address.match(/.+@.+\..+/s);
  else return 0;
};

export function isValidPhoneNumber(number) {
  if (number !== null) {
    const match = number.match(
      /(\(?([\d \-)–+/(]+){6,}\)?([ .\-–/]?)([\d]+))/g,
    );
    return match && match[0] === number;
  }
  return false;
}

export function sortById(arr) {
  // function compareFn(a, b) {
  //     return (a.id == b.id) ? 0 : ((a.id < b.id) ? -1 : 1)
  // }
  return [...arr].sort((a, b) => a.id - b.id);
}

export function generateString(length, onlyCapital = true) {
  let characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  if (!onlyCapital) characters = characters + "abcdefghijklmnopqrstuvwxyz";
  let result = "";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }

  return result;
}

export const THIS_YEAR = new Date().getFullYear();
export const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
export const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mrz",
  "Apr",
  "Mai",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Okt",
  "Nov",
  "Dez",
];

export const clone = (x) => JSON.parse(JSON.stringify(x));

export const checkIfValueIsEmpty = (val) =>
  val === null || val === "" || (Array.isArray(val) && val.length === 0);

export const zfill = (num, len) => (Array(len).join("0") + num).slice(-len);

export const convertToFormData = (form, fileKeys = [], excludeKeys = []) => {
  const formData = new FormData();
  Object.keys(form)
    .filter((key) => !excludeKeys.includes(key))
    .forEach((key) => {
      if (fileKeys.includes(key)) {
        if (!isString(form[key])) {
          // TODO: url
          const file = form[key];
          if (form[key]) formData.append(key, file, file.name); // null not possible
        }
      } else formData.append(key, form[key] !== null ? form[key] : "");
    });
  return formData;
};

export const getCalendarRange = (date, view) => {
  let rangeStart, rangeEnd;
  switch (view) {
    case "day":
      rangeStart = moment(date).startOf("day").toDate();
      rangeEnd = moment(date).endOf("day").toDate();
      break;
    case "week":
      rangeStart = moment(date).startOf("isoWeek").toDate();
      rangeEnd = moment(date).endOf("isoWeek").toDate();
      break;
    case "month":
      // add seven days because in the month view you can see the adjacent weeks
      rangeStart = moment(date).startOf("month").subtract(7, "days").toDate();
      rangeEnd = moment(date).endOf("month").add(7, "days").toDate();
      break;
    case "work_week":
      rangeStart = moment(date).startOf("isoWeek").toDate();
      rangeEnd = moment(rangeStart).add(5, "days").toDate();
      break;
    default:
      return null;
  }
  return { start: rangeStart, end: rangeEnd };
};

export const getNextDay = (n = 1) => {
  const today = new Date(); // get today's date
  return addNDays(today, n);
};

export const addNDays = (date, nDays) => {
  const newdate = new Date(date);
  newdate.setDate(date.getDate() + nDays); // add n days (for tomorrow 1)
  return newdate;
};

export const addNMonths = (date, nMonths) => {
  const newdate = new Date(date);
  newdate.setMonth(date.getMonth() + nMonths); // add n days (for tomorrow 1)
  return newdate;
};

export const addNYears = (date, nYears) => {
  const newdate = new Date(date);
  newdate.setFullYear(date.getFullYear() + nYears); // add n days (for tomorrow 1)
  return newdate;
};

export const addNMinutes = (date, nMinutes) => {
  return moment(date).add(nMinutes, "m").toDate();
};

export const roundToMinutes = (date, nMinutes, roundFunc = round) => {
  const _date = new Date(date);
  _date.setSeconds(0, 0);
  const min = _date.getMinutes();
  const div = roundFunc(min / nMinutes) * nMinutes;
  _date.setMinutes(div);
  return _date;
};

export const getDateRange = (startDate, endDate) => {
  const dates = [];
  let d = startDate;
  while (d <= endDate) {
    dates.push(d);
    d = addNDays(d, 1);
  }
  return dates;
};

export const getMonthFromDate = (defaultDate) => {
  // take first day of month to make it easier to calculate with it
  const date = new Date(defaultDate);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getYearFromDate = (defaultDate) => {
  // take first day of year to make it easier to calculate with it
  const date = new Date(defaultDate);
  date.setDate(1);
  date.setMonth(0);
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getMonthRange = (startMonth, endMonth) => {
  const months = [];
  let d = startMonth;
  while (d <= endMonth) {
    months.push(d);
    d = addNMonths(d, 1);
  }
  return months;
};

export const getYearRange = (startYear, endYear) => {
  const years = [];
  let d = startYear;
  while (d <= endYear) {
    years.push(d);
    d = addNYears(d, 1);
  }
  return years;
};

export const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const rgbToHex = (r, g, b) => {
  return (
    "#" +
    ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()
  );
};

export const hexToRgbaString = (hex, opacity = 1) => {
  const rgb = hexToRgb(hex);
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

export const hexTextColor = (hex) => {
  const rgbColor = hexToRgb(hex);
  return rgbColor.r * 0.299 + rgbColor.g * 0.587 + rgbColor.b * 0.114 > 186
    ? "#000000"
    : "#ffffff";
};

export const setColorOpacity = (color, newOpacity) => {
  const hexRegex = /^#([0-9A-Fa-f]{3}){1,2}$/;
  if (hexRegex.test(color)) color = hexToRgbaString(color);
  const updatedRgba = color.replace(
    /rgba\((\d+), (\d+), (\d+), (\d(\.\d+)?)\)/,
    (match, r, g, b, a) => {
      return `rgba(${r}, ${g}, ${b}, ${newOpacity})`;
    },
  );
  return updatedRgba;
};

export const isBoolean = (val) => val === true || val === false;

export const transpose = (arr) => {
  const nRows = arr.length;
  const nCols = arr[0].length;
  return arrange(nCols).map((c) => arrange(nRows).map((r) => arr[r][c]));
};

export const getUserName = (firstName, lastName) =>
  `${firstName.trim().replace(/ /g, "").toLowerCase()}_${lastName
    .trim()
    .replace(/ /g, "")
    .toLowerCase()}`;

export const mergeDeepInPlace = (target, ...sources) => {
  // Iterate through each source object
  sources.forEach((source) => {
    // Loop through each property in the source object
    for (const key in source) {
      // Check if the property is an object and not an array (you can customize this part for your use case)
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        // If the target does not have the key or is not an object, create an empty object
        if (!target[key] || typeof target[key] !== "object") {
          target[key] = {};
        }
        // Recursively merge nested objects
        mergeDeepInPlace(target[key], source[key]);
      } else {
        // Otherwise, just assign the value from the source to the target
        target[key] = source[key];
      }
    }
  });

  return target;
};

export const mergeDeep = (...sources) => {
  const target = {};
  sources.forEach((source) => {
    // Loop through each property in the source object
    for (const key in source) {
      // Check if the property is an object and not an array (you can customize this part for your use case)
      if (
        source[key] &&
        typeof source[key] === "object" &&
        !Array.isArray(source[key])
      ) {
        // If the target does not have the key or is not an object, create an empty object
        if (!target[key] || typeof target[key] !== "object") {
          target[key] = {};
        }
        // Recursively merge nested objects
        target[key] = mergeDeep(target[key], source[key]);
      } else {
        // Otherwise, just assign the value from the source to the target
        target[key] = source[key];
      }
    }
  });

  return target;
};

export const stringPadding = (number, length = 2, padString = "0") => {
  return String(number).padStart(length, padString);
};

export const datesEqual = (d1, d2) => {
  if ((d1 == null) !== (d2 == null)) return false;
  if (d1 == null) return false;
  return d1.valueOf() === d2.valueOf();
};

export const getLabelValuePair = (rawOptions) => {
  return rawOptions
    ? rawOptions.map((s) => {
        return isObject(s)
          ? s
          : {
              value: s,
              label: isString(s) ? s : s.toString(),
            };
      })
    : [];
};

export const cumArrSum = (arr) => {
  let total = 0;
  return arr.map((num) => (total += num)); // eslint-disable-line no-return-assign
};

export const formatNumber = (number) =>
  new Intl.NumberFormat("de-DE").format(number);

export const overlayColors = (color1, color2, p) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  const r = Math.round(rgb1.r * (1 - p) + rgb2.r * p);
  const g = Math.round(rgb1.g * (1 - p) + rgb2.g * p);
  const b = Math.round(rgb1.b * (1 - p) + rgb2.b * p);
  return rgbToHex(r, g, b);
};
